import React, { useState } from 'react';
import {
    Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails,
    Grid, Paper, TextField, InputAdornment, Chip, Stack, Button, Divider,
    Card, CardContent, Avatar, LinearProgress, Tooltip, Badge, useTheme, useMediaQuery
} from '@mui/material';
import {
    ExpandMore, Search, Help, Lightbulb, Security, Code,
    RecyclingRounded, SmartToyRounded, TrendingUpRounded,
    EmojiEventsRounded, GroupsRounded, NatureRounded,
    QuestionAnswerRounded, ThumbUpRounded, StarRounded,
    CameraAltRounded, DataUsageRounded, ScienceRounded
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import ContactSection from '../components/common/ContactSection';

const FAQPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = [
        { id: 'all', label: 'All Questions', icon: '🔍', color: '#43a047' },
        { id: 'getting-started', label: 'Getting Started', icon: '🚀', color: '#2196f3' },
        { id: 'ai-features', label: 'AI & Classification', icon: '🤖', color: '#9c27b0' },
        { id: 'rewards', label: 'Rewards & Points', icon: '🏆', color: '#ff9800' },
        { id: 'technical', label: 'Technical', icon: '⚙️', color: '#00b894' },
        { id: 'privacy', label: 'Privacy & Safety', icon: '🔒', color: '#e91e63' },
        { id: 'environmental', label: 'Environmental Impact', icon: '🌱', color: '#4caf50' }
    ];

    const faqs = [
        // Getting Started Category
        {
            category: 'getting-started',
            question: 'What is Sortify and how does it help the environment?',
            answer: 'Sortify is Bangladesh\'s first AI-powered smart waste classification system that helps you properly sort waste for environmental protection. By taking a photo of any waste item, our advanced machine learning model instantly identifies the waste type and provides specific disposal recommendations according to Bangladesh\'s waste management guidelines. This helps reduce environmental pollution, increases recycling rates, and promotes sustainable living practices.'
        },
        {
            category: 'getting-started',
            question: 'How do I start using Sortify?',
            answer: '1. Visit our website or download the app 2. Create a free account with your email 3. Verify your email address 4. Take a photo of any waste item 5. Get instant AI classification and disposal recommendations 6. Follow the suggested disposal method 7. Earn Champion Points for every classification! It\'s that simple to start making a positive environmental impact.'
        },
        {
            category: 'getting-started',
            question: 'Is Sortify really free to use?',
            answer: 'Yes! Sortify is completely free to use forever. You can classify unlimited waste items, track your environmental impact, earn Champion Points, compete on leaderboards, and access all educational content without any cost. We believe environmental responsibility should be accessible to everyone in Bangladesh.'
        },
        {
            category: 'getting-started',
            question: 'Do I need to create an account to use Sortify?',
            answer: 'While you can classify a few items without an account, creating a free account unlocks the full Sortify experience: track your classification history, earn Champion Points, level up from Rookie to Eco-Legend, compete on leaderboards, access personalized tips, and contribute to Bangladesh\'s environmental statistics.'
        },

        // AI & Classification Features
        {
            category: 'ai-features',
            question: 'How accurate is Sortify\'s AI classification system?',
            answer: 'Our AI model achieves over 90% accuracy and has been specifically trained on waste types common in Bangladesh. The model recognizes thousands of waste items including local products, packaging, and materials. However, we always recommend cross-checking with local waste management authorities for hazardous materials and following your area\'s specific disposal guidelines.'
        },
        {
            category: 'ai-features',
            question: 'What types of waste can Sortify identify?',
            answer: 'Sortify can identify and classify: • Recyclable waste (plastic bottles, paper, cardboard, metal cans, glass) • Organic waste (food scraps, fruit peels, vegetable waste, garden debris) • Hazardous waste (batteries, electronics, chemicals, medical waste) • General waste (non-recyclable items, mixed materials) • E-waste (phones, computers, cables) • Textile waste (clothes, fabric) We\'re continuously training our AI to recognize more waste types specific to Bangladesh.'
        },
        {
            category: 'ai-features',
            question: 'What should I do if the AI gives an incorrect classification?',
            answer: 'If you believe the classification is incorrect: 1. Use the "Report Issue" button on the result page 2. Provide the correct classification 3. Add any additional details 4. Our team reviews all feedback to improve the AI model 5. You still earn Champion Points for providing valuable feedback! Your corrections help make Sortify better for everyone in Bangladesh.'
        },
        {
            category: 'ai-features',
            question: 'Can Sortify work with poor quality images?',
            answer: 'While Sortify works best with clear, well-lit photos, our AI can handle various image conditions. For best results: • Ensure good lighting • Keep the waste item as the main focus • Avoid cluttered backgrounds • Take photos from a close distance • Use supported formats (JPG, PNG, WebP) If classification fails, try retaking the photo with better lighting.'
        },

        // Rewards & Points
        {
            category: 'rewards',
            question: 'What are Champion Points and how do I earn them?',
            answer: 'Champion Points are Sortify\'s reward system for environmental action! Earn points by: • Classifying waste items (+5 points) • Daily classification streaks (bonus points) • Providing feedback on classifications (+3 points) • Achieving high accuracy (+2 bonus points) • Completing weekly challenges (+15 points) Use points to level up from Rookie → Apprentice → Sorter → Pro Sorter → Master Sorter → Eco-Legend!'
        },
        {
            category: 'rewards',
            question: 'What are the different Champion levels and their benefits?',
            answer: 'Champion Levels and Requirements: • Rookie (0 pts) - Starting level, basic features • Apprentice (500 pts) - Access to advanced tips • Sorter (2,000 pts) - Unlock achievement badges • Pro Sorter (5,000 pts) - Leaderboard features • Master Sorter (15,000 pts) - Exclusive content • Eco-Legend (50,000 pts) - Recognition as environmental leader Each level unlocks new features and shows your commitment to environmental protection!'
        },
        {
            category: 'rewards',
            question: 'How do leaderboards work in Sortify?',
            answer: 'Sortify features multiple leaderboards: • Weekly Leaderboard - Top performers this week • Monthly Leaderboard - Monthly environmental champions • All-time Leaderboard - Lifetime contribution leaders Rankings are based on Champion Points earned, classification accuracy, and community contributions. Top performers get special recognition and inspire others to take environmental action!'
        },
        {
            category: 'rewards',
            question: 'Can I redeem Champion Points for real rewards?',
            answer: 'Currently, Champion Points are used for: • Leveling up your Champion status • Unlocking achievement badges • Accessing exclusive educational content • Competing on leaderboards • Getting recognition as an environmental leader We\'re exploring partnerships with eco-friendly brands for future reward redemptions!'
        },

        // Technical Questions
        {
            category: 'technical',
            question: 'What image formats and sizes does Sortify support?',
            answer: 'Sortify supports common image formats: • JPEG (.jpg, .jpeg) • PNG (.png) • WebP (.webp) Maximum file size: 10MB per image. Recommended image dimensions: 800x600 pixels minimum for best accuracy. The AI works with various orientations and automatically processes images for optimal classification results.'
        },
        {
            category: 'technical',
            question: 'Does Sortify work offline?',
            answer: 'Sortify requires an internet connection for AI classification as our machine learning models run on powerful cloud servers. However, you can: • View previously classified items offline • Read cached educational content • Access your classification history • Browse achievement badges We\'re exploring offline capabilities for future versions!'
        },
        {
            category: 'technical',
            question: 'Which browsers and devices are supported?',
            answer: 'Sortify works on: • Desktop browsers: Chrome, Firefox, Safari, Edge (latest versions) • Mobile browsers: Chrome Mobile, Safari Mobile, Samsung Internet • Tablets: iPad, Android tablets • Operating systems: Windows, macOS, Linux, iOS, Android We recommend using updated browsers for the best experience and security.'
        },

        // Privacy & Safety
        {
            category: 'privacy',
            question: 'Is my personal data safe with Sortify?',
            answer: 'Absolutely! Your privacy is our priority: • We only collect necessary data for app functionality • Images are processed securely and not stored permanently • Personal information is encrypted and protected • We never sell or share your data with third parties • You can delete your account and data anytime • We comply with international data protection standards'
        },
        {
            category: 'privacy',
            question: 'What happens to the photos I upload?',
            answer: 'Your uploaded photos are: • Temporarily processed by our AI for classification • Automatically deleted after 30 days • Never used for advertising or marketing • Not shared with third parties • Stored securely with encryption • Used only to improve AI accuracy (with your consent) You maintain full control over your uploaded content.'
        },
        {
            category: 'privacy',
            question: 'Can I use Sortify without providing personal information?',
            answer: 'Yes! You can use Sortify\'s basic classification features without an account. However, creating an account with minimal information (email only) unlocks: • Classification history • Champion Points and levels • Leaderboard participation • Personalized recommendations • Progress tracking We only ask for information that enhances your experience.'
        },

        // Environmental Impact
        {
            category: 'environmental',
            question: 'How does proper waste sorting help the environment?',
            answer: 'Proper waste sorting creates significant environmental benefits: • Reduces landfill waste by 40-60% • Increases recycling rates by enabling proper material recovery • Prevents soil and water contamination from hazardous materials • Reduces greenhouse gas emissions from waste decomposition • Conserves natural resources through increased recycling • Supports circular economy principles • Creates cleaner communities and healthier living environments'
        },
        {
            category: 'environmental',
            question: 'What is Sortify\'s environmental impact in Bangladesh?',
            answer: 'Since launch, Sortify users have: • Classified over 50,000 waste items correctly • Prevented an estimated 15 tons of recyclable materials from reaching landfills • Educated thousands of users about proper waste management • Contributed to a 25% improvement in waste sorting accuracy among active users • Supported local recycling initiatives through awareness campaigns We\'re proud to contribute to Bangladesh\'s environmental sustainability!'
        },
        {
            category: 'environmental',
            question: 'How can I maximize my environmental impact using Sortify?',
            answer: 'Maximize your impact by: • Using Sortify for all household waste decisions • Sharing the app with family and friends • Following all disposal recommendations • Participating in community challenges • Providing feedback to improve AI accuracy • Educating others about proper waste management • Achieving higher Champion levels • Joining local environmental initiatives Every correctly sorted item contributes to a cleaner Bangladesh!'
        }
    ];

    // Filter FAQs based on search and category
    const filteredFAQs = faqs.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
    };

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)' }}>
            {/* Hero Section - Mobile Responsive */}
            <Box sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                py: { xs: 4, sm: 6, md: 8 }
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="center">
                        <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    ❓ Frequently Asked Questions
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        opacity: 0.9,
                                        mb: { xs: 3, sm: 4 },
                                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                                        lineHeight: { xs: 1.3, sm: 1.4 },
                                        px: { xs: 1, sm: 0 }
                                    }}
                                >
                                    Find answers to common questions about Sortify's AI-powered waste classification system
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1, sm: 2 }}
                                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                                    justifyContent={{ xs: 'center', md: 'flex-start' }}
                                >
                                    <Chip
                                        label="🤖 AI-Powered Answers"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                    <Chip
                                        label="🇧🇩 Bangladesh Focused"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
                            <Box sx={{
                                textAlign: 'center',
                                fontSize: { xs: '4rem', sm: '6rem', md: '8rem' }
                            }}>
                                💡
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
                {/* Search and Filter Section - Mobile Responsive */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6 },
                        borderRadius: { xs: 3, sm: 4 },
                        background: 'linear-gradient(135deg, #ffffff, #f5fbf7)'
                    }}
                >
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        gutterBottom
                        color="#1b5e20"
                        sx={{
                            fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.25rem' },
                            textAlign: { xs: 'center', md: 'left' }
                        }}
                    >
                        🔍 Find Your Answer
                    </Typography>

                    {/* Search Bar - Mobile Responsive */}
                    <TextField
                        fullWidth
                        placeholder="Search for questions or topics..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="primary" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: { xs: 3, sm: 4 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: { xs: 2, sm: 3 },
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }
                        }}
                    />

                    {/* Category Filter - Mobile Responsive */}
                    <Typography
                        variant="h6"
                        fontWeight={600}
                        gutterBottom
                        color="#43a047"
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                            textAlign: { xs: 'center', md: 'left' }
                        }}
                    >
                        📂 Categories
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={{ xs: 1, sm: 2 }}
                        flexWrap="wrap"
                        sx={{ gap: { xs: 1, sm: 1.5 } }}
                    >
                        {categories.map((category) => (
                            <Chip
                                key={category.id}
                                label={`${category.icon} ${isSmallMobile ? category.label.split(' ')[0] : category.label}`}
                                onClick={() => handleCategoryChange(category.id)}
                                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                                sx={{
                                    borderColor: category.color,
                                    color: selectedCategory === category.id ? 'white' : category.color,
                                    backgroundColor: selectedCategory === category.id ? category.color : 'transparent',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                                    height: { xs: 32, sm: 36 },
                                    '&:hover': {
                                        backgroundColor: alpha(category.color, 0.1),
                                        borderColor: category.color
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                </Paper>

                {/* FAQ Results Count - Mobile Responsive */}
                <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: { xs: 'center', md: 'left' } }}>
                    <Typography
                        variant="h6"
                        color="#546e7a"
                        sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                    >
                        📊 Found {filteredFAQs.length} question{filteredFAQs.length !== 1 ? 's' : ''}
                        {selectedCategory !== 'all' && (
                            <Chip
                                label={categories.find(c => c.id === selectedCategory)?.label}
                                size="small"
                                sx={{ ml: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            />
                        )}
                    </Typography>
                </Box>

                {/* FAQ Accordions - Mobile Responsive */}
                <Stack spacing={{ xs: 2, sm: 3 }}>
                    {filteredFAQs.map((faq, index) => {
                        const category = categories.find(c => c.id === faq.category);
                        return (
                            <Accordion
                                key={index}
                                sx={{
                                    borderRadius: { xs: 2, sm: 3 },
                                    border: `2px solid ${alpha(category?.color || '#43a047', 0.2)}`,
                                    '&:before': { display: 'none' },
                                    '&.Mui-expanded': {
                                        borderColor: category?.color || '#43a047',
                                        boxShadow: `0 8px 32px ${alpha(category?.color || '#43a047', 0.15)}`
                                    }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMore sx={{ color: category?.color || '#43a047' }} />}
                                    sx={{
                                        py: { xs: 1, sm: 1.5 },
                                        px: { xs: 2, sm: 3 },
                                        '&.Mui-expanded': {
                                            backgroundColor: alpha(category?.color || '#43a047', 0.05)
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: category?.color || '#43a047',
                                                width: { xs: 32, sm: 40 },
                                                height: { xs: 32, sm: 40 },
                                                mr: { xs: 1.5, sm: 2 },
                                                fontSize: { xs: '1rem', sm: '1.2rem' }
                                            }}
                                        >
                                            {category?.icon || '❓'}
                                        </Avatar>
                                        <Typography
                                            variant="h6"
                                            fontWeight={600}
                                            sx={{
                                                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                                lineHeight: { xs: 1.3, sm: 1.4 },
                                                pr: 1
                                            }}
                                        >
                                            {faq.question}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: { xs: 2, sm: 3 },
                                        pt: 0,
                                        pb: { xs: 2, sm: 3 }
                                    }}
                                >
                                    <Box sx={{ ml: { xs: 5, sm: 7 } }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#546e7a',
                                                lineHeight: { xs: 1.6, sm: 1.7 },
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                whiteSpace: 'pre-line'
                                            }}
                                        >
                                            {faq.answer}
                                        </Typography>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>

                {/* No Results Message */}
                {filteredFAQs.length === 0 && (
                    <Paper
                        sx={{
                            p: { xs: 4, sm: 6 },
                            textAlign: 'center',
                            borderRadius: { xs: 3, sm: 4 },
                            background: 'linear-gradient(135deg, #fff3e0, #ffffff)'
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '2rem', sm: '3rem' },
                                mb: 2
                            }}
                        >
                            🤔
                        </Typography>
                        <Typography
                            variant="h5"
                            fontWeight={600}
                            gutterBottom
                            sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' } }}
                        >
                            No questions found
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            paragraph
                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                        >
                            Try adjusting your search terms or selecting a different category.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSearchTerm('');
                                setSelectedCategory('all');
                            }}
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                            Clear Filters
                        </Button>
                    </Paper>
                )}

                {/* Contact Section */}
                <Box sx={{ mt: { xs: 6, sm: 8 } }}>
                    <ContactSection />
                </Box>
            </Container>
        </Box>
    );
};

export default FAQPage;
